// App.js
import React, { useState, useEffect, forwardRef, useRef } from "react";
import { socket } from "./socket";
import LobbyArea from "./components/updated-ui/LobbyArea";
import GameArea from "./components/updated-ui/GameArea";
import {
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  Slide,
  AppBar,
  Toolbar,
  IconButton,
  List,
  Button,
  ListItem,
  Tooltip,
} from "@mui/material";
import CustomSnackbar from "./components/CustomSnackbar";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { v4 as uuidv4 } from "uuid";
import GameRules from "./components/GameRules";
import CustomNavbar from "./components/CustomNavbar";

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function App() {
  const [isConnected, setIsConnected] = useState(socket.connected);
  const [roomCode, setRoomCode] = useState(null);
  const [player, setPlayer] = useState(null);
  const [roomState, setRoomState] = useState(null);
  const [systemMessage, setSystemMessage] = useState("");
  const [gameCompleted, setGameCompleted] = useState(false);
  const [finalRoomInfo, setFinalRoomInfo] = useState(null);
  const [openRules, setOpenRules] = useState(false);
  const [copied, setCopied] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const playerIdRef = useRef(null);

  useEffect(() => {
    if (!playerIdRef.current) {
      let storedPlayerId = localStorage.getItem("playerId");
      if (!storedPlayerId) {
        storedPlayerId = uuidv4();
        localStorage.setItem("playerId", storedPlayerId);
      }
      playerIdRef.current = storedPlayerId;
      socket.playerId = storedPlayerId;
    }

    const savedRoomCode = localStorage.getItem("roomCode");
    if (savedRoomCode) {
      socket.emit("reconnectPlayer", {
        roomCode: savedRoomCode,
        playerId: playerIdRef.current,
      });
    }

    function onConnect() {
      setIsConnected(true);
      console.log("Connected.");
    }
    function onDisconnect() {
      setIsConnected(false);
      setSystemMessage("Disconnected. Trying to reconnect...");
    }
    function onRoomCreated({ roomCode, player }) {
      setRoomCode(roomCode);
      setPlayer(player);
      localStorage.setItem("roomCode", roomCode);
    }
    function onRoomJoined({ roomCode, player }) {
      setRoomCode(roomCode);
      setPlayer(player);
      localStorage.setItem("roomCode", roomCode);
    }
    function onRoomUpdate(updatedRoom) {
      setRoomState(updatedRoom);
      if (player) {
        const self = updatedRoom.players.find(
          (p) => p.playerId === player.playerId || p.id === player.id
        );
        if (self) setPlayer((prev) => ({ ...prev, ...self }));
      }
    }
    function onReconnectSuccess({ roomState, remainingTime }) {
      setRoomCode(roomState.roomCode);
      const self = roomState.players.find(
        (p) => p.playerId === playerIdRef.current
      );
      if (self) {
        setPlayer(self);
        setRoomState(roomState);
      } else {
        console.error("Player not found in room during reconnection");
        localStorage.removeItem("roomCode");
        setRoomCode(null);
        setPlayer(null);
        setRoomState(null);
        setSystemMessage("Failed to reconnect. Please join again.");
      }
    }
    function onReconnectFailed(message) {
      console.error("Reconnection failed:", message);
      localStorage.removeItem("roomCode");
      setRoomCode(null);
      setPlayer(null);
      setRoomState(null);
      setSystemMessage(`Reconnection failed: ${message}`);
    }
    function onGameCompleted(finalRoomState) {
      setFinalRoomInfo(finalRoomState);
      setGameCompleted(true);
    }
    function onGameEnded(message = "Game has ended.") {
      setSystemMessage(message);
      setTimeout(() => {
        localStorage.removeItem("roomCode");
        setRoomCode(null);
        setPlayer(null);
        setRoomState(null);
        setGameCompleted(false);
        setFinalRoomInfo(null);
      }, 3000);
    }
    function onYouAreHost() {
      setPlayer((prev) => ({ ...prev, isHost: true }));
      setSystemMessage("You are now the host!");
    }
    function onError(message) {
      setSystemMessage(`Error: ${message}`);
    }
    function onRoomNotFound() {
      setSystemMessage("Room not found.");
      localStorage.removeItem("roomCode");
    }
    function onRoomFull() {
      setSystemMessage("Room is full.");
    }
    function onSyncTimer() {}

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("roomCreated", onRoomCreated);
    socket.on("roomJoined", onRoomJoined);
    socket.on("roomUpdate", onRoomUpdate);
    socket.on("reconnectSuccess", onReconnectSuccess);
    socket.on("gameCompleted", onGameCompleted);
    socket.on("gameEnded", onGameEnded);
    socket.on("youAreHost", onYouAreHost);
    socket.on("yourRole", ({ role }) => setPlayer((p) => ({ ...p, role })));
    socket.on("systemMessage", ({ text }) => setSystemMessage(text));
    socket.on("error", onError);
    socket.on("roomNotFound", onRoomNotFound);
    socket.on("roomFull", onRoomFull);
    socket.on("syncTimer", onSyncTimer);

    if (!socket.connected) {
      socket.connect();
    }

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("roomCreated", onRoomCreated);
      socket.off("roomJoined", onRoomJoined);
      socket.off("roomUpdate", onRoomUpdate);
      socket.off("reconnectSuccess", onReconnectSuccess);
      socket.off("gameCompleted", onGameCompleted);
      socket.off("gameEnded", onGameEnded);
      socket.off("youAreHost", onYouAreHost);
      socket.off("yourRole");
      socket.off("systemMessage");
      socket.off("error");
      socket.off("roomNotFound");
      socket.off("roomFull");
      socket.off("syncTimer");
    };
  }, []);

  const handleCloseDialog = () => {
    if (player?.isHost) {
      socket.emit("endGame", { roomCode });
    }
    setGameCompleted(false);
    localStorage.removeItem("roomCode");
    setRoomCode(null);
    setPlayer(null);
    setRoomState(null);
    setFinalRoomInfo(null);
  };

  const currentRound = roomState?.currentRound ?? 0;
  const currentRole =
    roomState?.players?.find((p) => p.id === player?.id)?.role || player?.role;

  const handleOpenRules = () => setOpenRules(true);
  const handleCloseRules = () => setOpenRules(false);

  const handleCopyRoomCode = async () => {
    try {
      await navigator.clipboard.writeText(roomCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleLeaveRoom = () => {
    if (window.confirm("Are you sure you want to leave the room?")) {
      socket.emit("leaveRoom");
      localStorage.removeItem("roomCode");
      setRoomCode(null);
      setPlayer(null);
      setRoomState(null);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: theme.palette.background.default,
        backgroundImage: "url('/assets/images/rmcs-background.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          zIndex: 0,
        },
      }}
    >
      <Box
        sx={{
          width: "100%",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          zIndex: 1,
        }}
      >
        <CustomNavbar
          leftContent={
            <Typography
              variant="h6"
              sx={{
                opacity: 0.8,
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              {roomCode && player && (
                <>
                  Room:&nbsp;
                  <Tooltip title={copied ? "Copied!" : "Click to copy"} arrow>
                    <Button
                      onClick={handleCopyRoomCode}
                      variant="text"
                      size="small"
                      sx={{
                        textTransform: "none",
                        minWidth: 0,
                        px: 0.5,
                        py: 0,
                        fontWeight: 600,
                        fontSize: "inherit",
                      }}
                    >
                      {roomCode}
                      <ContentCopyIcon
                        fontSize="inherit"
                        sx={{ ml: 0.5, fontSize: "0.85em", opacity: 0.7 }}
                      />
                    </Button>
                  </Tooltip>
                  &nbsp;| Round: {currentRound}/{roomState?.totalRounds}
                </>
              )}
            </Typography>
          }
          centerContent={
            roomState?.status === "playing" && player ? (
              <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
                {player.username}
                {currentRole !== "None" && (
                  <Box
                    component="span"
                    color="error.main"
                    sx={{ fontWeight: 800, ml: 1, fontSize: "inherit" }}
                  >
                    ({currentRole})
                  </Box>
                )}
              </Typography>
            ) : (
              <Typography variant="h6" component="h1" sx={{ fontWeight: 600 }}>
                RMCS (RAJA, MANTRI, CHOR & SIPAHI)
              </Typography>
            )
          }
          rightContent={
            <>
              {roomCode && (
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  onClick={handleLeaveRoom}
                  sx={{ textTransform: "none", py: 0.5, mr: 2 }}
                >
                  Leave Room
                </Button>
              )}
              <Button
                variant="outlined"
                size="small"
                onClick={handleOpenRules}
                sx={{ textTransform: "none", py: 0.5 }}
              >
                Rules
              </Button>
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: "50%",
                  backgroundColor: isConnected ? "success.main" : "error.main",
                  border: "1px solid",
                  borderColor: isConnected ? "success.dark" : "error.dark",
                  ml: 1,
                }}
                title={isConnected ? "Connected" : "Disconnected"}
              />
            </>
          }
        />

        <Dialog
          open={openRules}
          onClose={handleCloseRules}
          maxWidth="md"
          fullWidth
          scroll="paper"
        >
          <DialogTitle sx={{ m: 0, p: 2 }}>
            Game Rules
            <IconButton
              aria-label="close"
              onClick={handleCloseRules}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <GameRules />
          </DialogContent>
        </Dialog>

        <CustomSnackbar message={systemMessage} setMessage={setSystemMessage} />

        {!roomCode || !player ? (
          <LobbyArea
            setRoomCode={setRoomCode}
            setPlayer={setPlayer}
            socket={socket}
          />
        ) : (
          <GameArea
            roomCode={roomCode}
            player={player}
            roomState={roomState}
            socket={socket}
            setSystemMessage={setSystemMessage}
          />
        )}

        <Dialog
          fullScreen
          open={gameCompleted}
          onClose={() => {}}
          TransitionComponent={Transition}
          PaperProps={{
            sx: {
              backgroundColor: theme.palette.background.default,
              color: theme.palette.text.primary,
            },
          }}
        >
          <AppBar
            sx={{
              position: "relative",
              backgroundColor: theme.palette.background.paper,
              height: 48,
            }}
          >
            <Toolbar variant="dense" sx={{ minHeight: 48 }}>
              <Typography sx={{ ml: 2, flex: 1 }} variant="subtitle1">
                Game Completed!
              </Typography>
              {!player?.isHost && (
                <Button autoFocus color="inherit" onClick={handleCloseDialog}>
                  Leave
                </Button>
              )}
            </Toolbar>
          </AppBar>

          <Box sx={{ p: 4 }}>
            <Typography variant="h4" gutterBottom align="center">
              Final Results
            </Typography>

            {finalRoomInfo && (
              <>
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" gutterBottom>
                    Final Scores:
                  </Typography>
                  <List>
                    {finalRoomInfo.players
                      .sort((a, b) => b.score - a.score)
                      .map((player, index) => (
                        <ListItem key={player.id}>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: index === 0 ? 700 : 400,
                              color:
                                index === 0 ? "warning.main" : "text.primary",
                            }}
                          >
                            {index + 1}. {player.username} - {player.score}{" "}
                            points
                            {index === 0 && " 🏆"}
                          </Typography>
                        </ListItem>
                      ))}
                  </List>
                </Box>

                {player?.isHost ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      gap: 2,
                      mt: 2,
                    }}
                  >
                    <Button
                      variant="contained"
                      onClick={handleCloseDialog}
                      color="error"
                      size="large"
                      sx={{ px: 4, py: 1.5 }}
                    >
                      End Game for Everyone
                    </Button>
                  </Box>
                ) : (
                  <Typography variant="body1" align="center" sx={{ mt: 2 }}>
                    Waiting for host to end the game...
                  </Typography>
                )}
              </>
            )}
          </Box>
        </Dialog>
      </Box>
    </Box>
  );
}

export default App;
